// How long one round of an Oireachtas competition takes on stage (card
// 524449bc, plan Phase 1). The schedule's unit is a competition-round: the
// hard-shoe, soft-shoe and set rounds of a championship are placed apart
// (ECRO 2025 stage schedule). Each card's kind comes from the server's round
// spec (helpers/oireachtasRoundSpec.js); this file only turns kind, dancers
// and dancers-at-a-time into minutes, with the feis schedule's option E
// constants (scheduleTiming.js) so both schedules estimate alike.
//
//   first round / one-round competition: 60 s call and line-up
//   later round (round change, recall announcement): 270 s
//   + ceil(dancers / at a time) x (music + changeover)
//   treble reel: every dancer 16 bars in turn, then 8 bars all together
import { C } from '../../ManageFeisGrade/RedirectFeis/Schedule/scheduleTiming';

const musicSec = (bars, speed, beats = 2) => (bars * beats * 60) / speed;

export const TRAD_SET_SEC = 66; // a traditional set (scheduleTiming DANCES)
export const FIGURE_SEC = 330; // figure choreography, about 5 min plus entry
export const DRAMA_SEC = 480; // dance drama, about 8 min
const TEAM_SEC = { 2: 60, 3: 75, 4: 120, 6: 150, 8: 180 }; // scheduleTiming FIGURES
const REEL_SEC = musicSec(16, 113); // 16 bars of a reel @113 for each dancer
const REEL_FINISH_SEC = musicSec(8, 113); // the 8 bars everyone dances together

// Seconds of music for one group of a card, and what to call it.
export const groupMusic = (card) => {
  switch (card?.kind) {
    case 'hard':
      return { sec: C.HEAVY, music: 'treble jig or hornpipe, about 97 s' };
    case 'soft':
      return { sec: C.SOFT, music: 'reel or slip jig, about 68 s' };
    case 'set':
      return card.trad ? { sec: TRAD_SET_SEC, music: 'traditional set, about 66 s' } : { sec: C.SET, music: 'set dance, about 90 s' };
    case 'team': {
      if (card.team === 'figure') return { sec: FIGURE_SEC, music: 'figure choreography, about 5 min' };
      if (card.team === 'drama') return { sec: DRAMA_SEC, music: 'dance drama, about 8 min' };
      const sec = TEAM_SEC[card.teamSize] || TEAM_SEC[4];
      return { sec, music: `${card.teamSize || 4}-hand, about ${sec} s` };
    }
    default:
      return { sec: C.UNKNOWN, music: 'dance not known: about a reel' };
  }
};

const clampK = (k) => {
  const n = Math.floor(Number(k));
  return n >= 1 && n <= 6 ? n : null;
};

// Dancers (or teams) on stage at once: the card's own setting, else the spec's default.
export const atATimeOf = (card, override) => clampK(override) || clampK(card?.defaultAtATime) || 2;

// Dancers a card is expected to have. Round 3 of a championship is the recall:
// 50% when 21 or more dance, everyone at 20 or fewer (ECRO Rule 10), everyone
// in a recall-all competition (SC-06). Before the day this is an estimate.
export const expectedDancers = (card, entrants) => {
  const n = Math.max(0, Math.floor(Number(entrants) || 0));
  if (!card?.recall) return n;
  if (card.recallAll || n <= 20) return n;
  return Math.ceil(n / 2);
};

// The estimate for one card, with its parts for the timing panel. Seconds throughout.
export const estimateCard = (card, entrants, override) => {
  const n = expectedDancers(card, entrants);
  const overheadSec = card?.round > 1 ? C.ROUND : C.COMP;
  const continuous = card?.kind === 'reel_continuous';
  // nobody entered: no time, but the card keeps its dancers-at-a-time setting
  // (code review R2-F2)
  if (n === 0) return { n, k: continuous ? 1 : atATimeOf(card, override), groups: 0, overheadSec: 0, sec: 0, totalSec: 0, minutes: 0, music: continuous ? '16 bars @ 113 each, then 8 bars together' : groupMusic(card).music, continuous };
  if (continuous) {
    const sec = n * REEL_SEC + REEL_FINISH_SEC;
    const totalSec = overheadSec + sec;
    return { n, k: 1, groups: n, overheadSec, sec, totalSec, minutes: Math.max(1, Math.round(totalSec / 60)), music: '16 bars @ 113 each, then 8 bars together', continuous: true };
  }
  const k = atATimeOf(card, override);
  const { sec: perGroupSec, music } = groupMusic(card);
  const groups = Math.ceil(n / k);
  const change = card?.kind === 'team' ? C.W_GRADE : C.W_CHAMP;
  const sec = groups * (perGroupSec + change);
  const totalSec = overheadSec + sec;
  return { n, k, groups, perGroupSec, overheadSec, sec, totalSec, minutes: Math.max(1, Math.round(totalSec / 60)), music };
};

export const cardMinutes = (card, entrants, override) => estimateCard(card, entrants, override).minutes;
